/**
 * Admin Organization Detail Page
 *
 * Shows organization details with tabbed navigation for:
 * - Details (core settings)
 * - Connections (provider integrations)
 * - Users (member management)
 * - Projects (organization projects)
 */

import { Card, Center, Stack, Text } from '@cortex/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import {
  EnvironmentVariablesTab,
  OrganizationConnectionsTab,
  OrganizationDetailsTab,
  OrganizationHeader,
  OrganizationProjectsTab,
  OrganizationTabs,
  OrganizationUsersTab,
} from '@/components/admin';
import { useFetchWithAuth } from '@/hooks/use-api';

const organizationSearchSchema = z.object({
  tab: z.string().optional(),
});

export const Route = createFileRoute('/_app/admin/organizations/$slug')({
  validateSearch: organizationSearchSchema,
  component: OrganizationDetailPage,
});

interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  billing_email: string | null;
  created_at: string;
  updated_at: string;
}

function OrganizationDetailPage() {
  const { slug } = Route.useParams();
  const { tab } = Route.useSearch();
  const fetchWithAuth = useFetchWithAuth();
  const activeTab = tab || 'details';
  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch organization data by slug
  useEffect(() => {
    const fetchOrganization = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchWithAuth(`/admin/organizations/${slug}`);
        if (!response.ok) {
          throw new Error('Failed to fetch organization');
        }

        const { data } = await response.json();
        setOrg(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load organization'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [slug, fetchWithAuth]);

  // Refresh organization data
  const refreshOrganization = async () => {
    try {
      const response = await fetchWithAuth(`/admin/organizations/${slug}`);
      if (!response.ok) {
        throw new Error('Failed to fetch organization');
      }

      const { data } = await response.json();
      setOrg(data);
    } catch {
      // Silently handle fetch failure - UI shows stale data
    }
  };

  if (loading) {
    return (
      <Center minHeight="24rem">
        <Text color="muted">Loading...</Text>
      </Center>
    );
  }

  if (error || !org) {
    return (
      <Center minHeight="24rem">
        <Stack gap="md" align="center">
          <Text color="error">{error || 'Organization not found'}</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="lg" className="p-6">
      {/* Header with logo, name, and back link */}
      <OrganizationHeader organization={org} />

      {/* Tab navigation */}
      <OrganizationTabs organizationSlug={org.slug} activeTab={activeTab} />

      {/* Tab content */}
      <Card className="p-6">
        {activeTab === 'details' && (
          <OrganizationDetailsTab
            organization={org}
            onUpdate={refreshOrganization}
          />
        )}

        {activeTab === 'connections' && (
          <OrganizationConnectionsTab organizationId={org.id} />
        )}

        {activeTab === 'users' && (
          <OrganizationUsersTab organizationId={org.id} />
        )}

        {activeTab === 'projects' && (
          <OrganizationProjectsTab organizationId={org.id} />
        )}

        {activeTab === 'environment' && (
          <EnvironmentVariablesTab organizationId={org.id} />
        )}
      </Card>
    </Stack>
  );
}
